import { groupBy } from 'lodash';
import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { Subject, Observable, zip } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { TableColumn } from '../_offline/models/report-preview/report-template';
import { DYNAMIC_SELECT_ITEM, IMAGE_TYPE, REGULAR_EXPRESSION } from '../app.constants';
import { InspectionReport, ReportLocation } from '../_objects/inspection-report';
import { ReportElement, ElementType, Location, ReportProperties, ReportLine, ReportImage, ReportRectangle, ReportSignature } from '../_objects/report-element';
import { SubjectLocation, SubjectModel } from '../_objects/subject-model';
import { PreviewImageDetail } from '../_offline/models/report-preview/preview-image-detail';
import { ReportCustomElement } from '../_offline/models/report-preview/report-custom-element';
import { ReportDataGenerator } from '../_offline/models/report-preview/report-data-generator';
import { ReportDataPlate } from '../_offline/models/report-preview/report-data-plate';
import { ReportDropDownList } from '../_offline/models/report-preview/report-dropdown-list';
import { ReportISO } from '../_offline/models/report-preview/report-iso';
import { ReportMeasurementPoint } from '../_offline/models/report-preview/report-measurement-point';
import { ReportModel } from '../_offline/models/report-preview/report-model';
import { ReportPage } from '../_offline/models/report-preview/report-page';
import { ReportSection } from '../_offline/models/report-preview/report-section';
import { ReportSummary } from '../_offline/models/report-preview/report-summary';
import { ReportTable } from '../_offline/models/report-preview/report-table';
import { ReportTemplate, TemplateCustomPageSection, ReportCustomElements, TemplateDefinition, ReportTitleFormat, TemplatePageSection, ResponseIdList, TemplateCommonSection, ListFormat, ReportTemplateImageList } from '../_offline/models/report-preview/report-template';
import { ReportText } from '../_offline/models/report-preview/report-text';
import { ReportVisualEvents } from '../_offline/models/report-preview/report-visual-events';
import { ImageComponent, TableData } from '../_offline/models/report-preview/TableData';
import { IndividualReportDbService } from './database/individual-report-db.service';
import { IndividualReportTemplateDbService } from './database/individual-report-template-db.service';
import { InspectionDbService } from './database/inspection-db.service';
import { InspectionImageDbService } from './database/inspection-image-db.service';
import { TenantConfigurationDbService } from './database/tenant-configuration-db.service';
import { TenantDetailDbService } from './database/tenant-detail-db.service';
import { UserDbService } from './database/user-db.service';
import { InspectionImageOfflineService } from './offline/inspection-image-offline.service';
import { InspectionReportOfflineService } from './offline/inspection-report-offline.service';
import { LoginUserOfflineService } from './offline/login-user-offline.service';
import { ReportTemplateOfflineService } from './offline/report-template-offline.service';
import { ReportPreviewContentService } from './report-preview-content.service';
import { JSONSyntaxValidator } from './report-template-design.service';
import { UtilsService } from './_utils.service';
import { DynamicProperty, DYNAMIC_PROPERTY } from '../_objects/dynamic-property';
import { InspectionSchema } from '../_offline/models/inspection.schema';
import { TenantConfigSchema } from '../_offline/models/tenantConfiguration.schema';

export interface Notifier {
  componentName: string;
  ids: NotifierIds;
}

export interface NotifierIds {
  pageId: string;
  elementId: string;
}

export type FieldType =
  | 'ReadOnly'
  | 'Text'
  | 'TextArea'
  | 'Number'
  | 'Integer'
  | 'DateTime'
  | 'CheckBox'
  | 'Select'
  | 'JSON'
  | 'Multi'
  | 'List'
  | 'Color'
  | 'TableHeader'
  | 'PriorityItems'
  | 'TableColumn'
  | 'SubTotal'
  | 'DropDownOptionItems'
  | 'ImageInfo';
export type TypeOfJSON = string | number | boolean | object;

export interface PropertyPreviewMetadata {
  name: string;
  parentName: string;
  value: TypeOfJSON | Array<TypeOfJSON>;
  isRequired: boolean;
  isEdited: boolean;
  selectItems: { label: string; value?: string | number }[];
  category?:
  | 'GENERAL'
  | 'TABLE'
  | 'COLUMN'
  | 'ROW'
  | 'TITLE'
  | 'SUBTOTAL'
  | 'IMAGEINFO';
  group?: 'Position' | 'Size' | 'TextBox' | 'Line' | 'Background' | 'Font';
  isFieldType(type: FieldType): boolean;
}

export interface ReportPreviewMetadata {
  topPx: number;
  leftPx: number;
  widthPx: number;
  heightPx: number;
}

export interface UpdateMetadata {
  id: string;
  name: string;
  parentName?: string;
  value: TypeOfJSON | Array<TypeOfJSON>;
  oldValue: TypeOfJSON | Array<TypeOfJSON>;
}

export interface MousePoint {
  onElementX: number;
  onElementY: number;
}

export enum DataProperty {
  WorstOnly = 0,
  All = 1
}

@Injectable({ providedIn: 'root' })
export class OfflineReportPreviewService {
  get reportProperties(): ReportElement[] {
    if (!this.reportModel) {
      return null;
    }

    const result = Object.entries(this.reportModel.ElementFinder);
    return result.map((_) => _[1]);
  }

  private _isSaveHtml = false;
  private _isPreview = false;

  get tenantId() {
    return this.loginUserOfflineService.tenantId;
  }

  // PDF生成用のHTMLならTrue
  get isSaveHtml() {
    return this._isSaveHtml;
  }

  set isSaveHtml(value: boolean) {
    this._isSaveHtml = value;
  }

  // DraftPdfならTrue
  get isPreview() {
    return this._isPreview;
  }

  set isPreview(value: boolean) {
    this._isPreview = value;
  }

  constructor(
    private reportTemplateService: ReportTemplateOfflineService,
    private reportService: InspectionReportOfflineService,
    private individualReportTemplateDbService: IndividualReportTemplateDbService,
    private reportDbService: IndividualReportDbService,
    private configDbService: TenantConfigurationDbService,
    private inspectionDbService: InspectionDbService,
    private inspectionImageDbService: InspectionImageDbService,
    private userDbService: UserDbService,
    private tenantDbService: TenantDetailDbService,
    private reportPreviewContentService: ReportPreviewContentService,
    private loginUserOfflineService: LoginUserOfflineService,
    private inspectionImageService: InspectionImageOfflineService,
    private utils: UtilsService,
  ) {
    this.onReady$ = new Subject();
    this.onReady = this.onReady$.asObservable().pipe(filter(_ => _ !== null));

    this.onSelectedElement$ = new Subject();
    this.onSelectedElement = this.onSelectedElement$.asObservable();

    this.onEndDraw$ = new Subject();
    this.onEndDraw = this.onEndDraw$.asObservable();

    this.onEndUpdate$ = new Subject();
    this.onEndUpdate = this.onEndUpdate$.asObservable();

    this.virtualGridPx = 15; // 仮値。
  }
  public static PROP_DYNAMIC_ATTR4VISOL: string[] = [
    'text',
    'type',
    'dynamicId',
    'editableId',
    'inputType',
    'validation',
    'format',
    'required',
    'maxLength',
    'radioType',
    'radioLabel',
    'options',
    'label',
    'value',
    'inputGroup',
    'src',
    'multiSelectDelimiter'
  ];
  public static PROP_DYNAMIC_ATTR4IPM: string[] = [
    'text',
    'type',
    'dynamicId',
    'editableId',
    'inputType',
    'validation',
    'format',
    'required',
    'maxLength',
    'radioType',
    'radioLabel',
    'options',
    'label',
    'value',
    'inputGroup',
    'src',
    'stageId',
    'itemDataSource',
    'captionsList',
    'titleText',
    'isStriped',
    'tableRules',
    'columns',
    'reservedFor',
    'isoType',
    'dynamicDataField'
  ];

  public static PROP_DYNAMIC_DATEFORMAT: { label: string; value?: string }[] = [
    { label: '01/01/2019', value: 'MM/dd/yyyy' },
    { label: '1/1/2019', value: 'M/d/yyyy' },
    { label: '1/1/19', value: 'M/d/yy' },
    { label: '2019/1/1', value: 'yyyy/M/d' },
    { label: '19/1/1', value: 'yy/M/d' },
    { label: '2019-01-01', value: 'yyyy-MM-dd' },
    { label: '2019-1-1', value: 'yyyy-M-d' },
    { label: '19-01-01', value: 'yy-MM-dd' },
    { label: 'January 1, 2019', value: 'MMMM d, yyyy' },
    { label: 'January 01, 2019', value: 'MMMM dd, yyyy' },
    { label: 'Tuesday, January 01, 2019', value: 'dddd, MMMM dd, yyyy' },
    { label: 'Tuesday, Jan 1, 2019', value: 'dddd, MMM d, yyy' },
    { label: 'Tues, Jan 1, 2019', value: 'ddd, MMM d, yyyy' },
  ];
  public static PROP_DYNAMIC_PAGEFORMAT: { label: string; value?: string }[] = [
    { label: '1/10', value: '$/#' },
    { label: '1', value: '$' },
    { label: 'p.1', value: 'p.$' },
    { label: 'Page 1 of 10', value: 'Page $ of #' },
  ];

  private templateId: string;
  private isVisol = false;

  public originTemplate: ReportTemplate;
  private tempTemplate: ReportTemplate;
  private report: InspectionReport;
  private reportModel: ReportModel;
  private templateImages: ReportTemplateImageList[];
  private dynamicDataFields: any;
  private stageNames: { label: string; value?: string }[];
  public templateForUndo: ReportTemplate[];
  public templateForRedo: ReportTemplate[];
  public highlightElement: boolean;
  public checkedRuler = false;
  public showVTInTable = false;
  public customTemplateDefinition: TemplateCustomPageSection;
  public reportTemplateId: string;
  public customElementPageKey: string;
  public isRedirectingToCustomElement: boolean;
  public reportCustomTemplates: ReportCustomElements[];
  public clickedPageIdForDelete: string;
  public inspectionSchema: InspectionSchema;
  public configSchemas: TenantConfigSchema[];

  private onReady$: Subject<string>;
  public onReady: Observable<string>;

  private onSelectedElement$: Subject<string>;
  public onSelectedElement: Observable<string>;

  private onEndDraw$: Subject<string>;
  public onEndDraw: Observable<string>;

  private onEndUpdate$: Subject<string>;
  public onEndUpdate: Observable<string>;

  private readonly virtualGridPx: number;
  public drawElementType: ElementType;
  public drawDesignCustomElementId: string;
  public drawDesignCustomElementName: string;
  public isInitializing = false;
  public picturePageBreakRow: number;

  public inspectionSummary: TableData[][];
  public inspectionPictures: TableData[][];
  public inspectionDataPlate: ImageComponent[][];
  private subtotal: { [name: number]: number };
  public previewImages: PreviewImageDetail[] = [];

  public initialize(reportTemplateId: string, reportId: string, version: number) {
    this.reportPreviewContentService.reportLocationsWithDummy = [];
    this.isInitializing = true;
    this.templateId = reportTemplateId;
    this.isVisol = true;
    let reportTemplate: ReportTemplate;
    zip(
      this.reportTemplateService.getReportTemplate(this.templateId),
      this.reportService.getInspectionReport(reportId, version),
    )
      .pipe(switchMap(values => {
        reportTemplate = values[0];
        this.report = values[1];
        this.removeExcludedInputGroupsFromTemplate(reportTemplate);
        this.loadTemplate(reportTemplate);
        const inspectionId = this.report.inspectionIds[0];
        return zip(
          this.individualReportTemplateDbService.get(this.templateId),
          this.inspectionDbService.getInspection(inspectionId, true),
          this.inspectionImageDbService.getAll(inspectionId),
          this.reportDbService.get(this.report.reportId),
          this.configDbService.getAll(),
          this.userDbService.getAll(),
          this.tenantDbService.get()
        );
      }))
      .pipe(switchMap(values => {
        const reportTemplateSchema = values[0];
        const inspectionSchema = values[1];
        const imagesSchemas = values[2];
        const reportSchema = values[3];
        const configSchemas = values[4];
        const userSchemas = values[5];
        const tenantSchema = values[6];
        this.reportPreviewContentService
          .setData(reportTemplateSchema, inspectionSchema, imagesSchemas, reportSchema, configSchemas, userSchemas, tenantSchema);

        const obs = this.reportPreviewContentService.imageSchemas
          .map(schema => this.inspectionImageService.getInspectionImage(schema.inspectionId, schema.id));
        this.configSchemas = configSchemas;
        return zip(...obs);
      }))
      .subscribe(images => {
        this.previewImages = [];
        const inspectionSchema = this.reportPreviewContentService.inspectionSchema;
        this.inspectionSchema = inspectionSchema;
        const parsedInspectionLocations = JSON.parse(this.inspectionSchema.inspectionLocations);
        const inspectionImageSchemas = this.reportPreviewContentService.imageSchemas;
        const reportLocations = this.report.reportLocations;
        const subjectModel = new SubjectModel();
        subjectModel.setSubjectModelSchema(inspectionSchema.subject.subjectModel);
        const locList = this.utils.getLocations(subjectModel);

        images.forEach(image => {
          const schema = inspectionImageSchemas.find(imgSchema => imgSchema.id === image.id);
          if (schema) {
            const measurementItems = schema.measurementItems !== null
              ? JSON.parse(schema.measurementItems)
              : [];
            const result = new PreviewImageDetail(image, schema.width, schema.height, false, schema.tenantId, measurementItems);
            this.previewImages.push(result);
          }
        });

        parsedInspectionLocations.forEach(item => {
          if (!reportLocations.some( loc => loc.identifier === item.identifier)) {
            reportLocations.push({
              identifier: item.identifier,
              imageIds: []
            });
          }
        });

        reportLocations.forEach(location => {
          if (location.imageIds.length < 1 && locList.some(loc => loc.identifier === location.identifier)) {
            const dummy = new PreviewImageDetail();
            dummy.id = this.generateUUID();
            dummy.inspectionId = inspectionSchema.id;
            dummy.subjectLocationId = inspectionSchema.id;
            dummy.createdBy = this.loginUserOfflineService.loginUser.id;
            dummy.thumbFilePath = '';
            dummy.setExtendProp(85, 68, true, this.tenantId, []);
            this.previewImages.push(dummy);
            location.imageIds.push(dummy.id);
          }
        });

        this.reportPreviewContentService.reportLocationsWithDummy = reportLocations;

        if (this.previewImages.length > 0) {
          this.previewImages = this.previewImages
            .sort((a, b) => this.sortPreviewImage(a, b));
        }
        this.calcSummaryPage(this.previewImages, reportLocations, subjectModel, locList);
        this.calcPicturePage(this.previewImages, reportLocations, subjectModel);
        this.calcDataPlatePage(this.previewImages, reportLocations, subjectModel);
        this.reportModel.previewPageFactory(this.inspectionSummary, this.inspectionPictures, this.inspectionDataPlate);
        this.isInitializing = false;
        this.reportTemplateService.inspectionSummary = this.inspectionSummary;
        this.reportTemplateService.previewImages = this.previewImages;
        this.ready();
      });
  }

  public loadTemplate(template: ReportTemplate) {
    this.loadReportTemplate(template);
    this.loadReportTemplateImages();
    this.dynamicDataFields = DYNAMIC_SELECT_ITEM;
  }

  public loadReportTemplate(template: ReportTemplate, _isUndo?: boolean) {
    this.tempTemplate = template;
    this.originTemplate = JSON.parse(JSON.stringify(template, undefined, 4));
    this.reportModel = new ReportModel(template.template);
    this.onEndUpdate$.next('Updated');
  }

  public reloadReportTemplate(template: ReportTemplate, isUndo?: boolean) {
    this.tempTemplate = template;
    this.reportModel = new ReportModel(template.template);
    this.onEndUpdate$.next('Updated');
    if (isUndo) {
      this.ready();
    }
  }

  public reloadTemplate(template: ReportTemplate) {
    this.loadTemplate(template);
  }

  public getOriginTemplate(): ReportTemplate {
    return this.originTemplate;
  }
  public getTempTemplate(): ReportTemplate {
    return this.tempTemplate;
  }
  public getTemplateId(): string {
    return this.tempTemplate.id;
  }
  public getTemplateName(): string {
    return this.tempTemplate.name;
  }
  public getTemplateList(): ListFormat[] {
    return this.tempTemplate.template.list;
  }
  public getTemplateIsValid(): boolean {
    return this.tempTemplate.isValid;
  }
  public getTemplateReportTitleFormat(): ReportTitleFormat[] {
    return this.tempTemplate.reportTitleFormat;
  }
  public getTemplateReportTitleInputMode(): string {
    return this.tempTemplate.reportTitleInputMode;
  }
  public getTemplateSubjectModelIds(): string[] {
    return this.tempTemplate.subjectModelIds;
  }
  public getTemplateReportType(): string {
    return this.tempTemplate.reportType;
  }
  public getTemplateEquipmentType(): string[] | string {
    return this.tempTemplate.equipmentType;
  }
  public getTemplateFontFamily(): string {
    return this.tempTemplate.template.fontFamily;
  }
  public getTemplateFontSize(): number {
    return this.tempTemplate.template.fontSize;
  }
  public setTemplateName(name: string) {
    this.tempTemplate.name = name;
  }
  public setTemplateIsValid(isValid: boolean) {
    this.tempTemplate.isValid = isValid;
  }
  public setTemplateReportTitleFormat(reportTitleFormat: ReportTitleFormat[]) {
    this.tempTemplate.reportTitleFormat = reportTitleFormat;
  }
  public setTemplateReportList(reportObservation: ListFormat[]) {
    this.tempTemplate.template.list = reportObservation;
  }
  public setTemplateReportTitleInputMode(reportTitleInputMode: string) {
    this.tempTemplate.reportTitleInputMode = reportTitleInputMode;
  }
  public setTemplateSubjectModelIds(subjectModelIds: string[]) {
    this.tempTemplate.subjectModelIds = subjectModelIds;
  }
  public isChangeTemplate(): boolean {
    if (this.originTemplate && this.tempTemplate) {
      const originJson = JSON.stringify(
        this.originTemplate.template,
        undefined,
        4
      );
      const tempJson = JSON.stringify(this.getTemplate(), undefined, 4);
      return (
        originJson !== tempJson ||
        this.originTemplate.name !== this.tempTemplate.name ||
        this.originTemplate.isValid !== this.tempTemplate.isValid ||
        this.originTemplate.reportTitleInputMode !==
        this.tempTemplate.reportTitleInputMode ||
        JSON.stringify(this.originTemplate.reportTitleFormat, undefined, 4) !==
        JSON.stringify(this.tempTemplate.reportTitleFormat, undefined, 4)
      );
    } else {
      return false;
    }
  }

  // editableIdの入力文字チェック
  public isLowerCamelCase(textField): boolean {
    return REGULAR_EXPRESSION.SMALL_LETTERS_PATTERN.test(textField[0]);
  }

  // FieldType:Text
  public validatorText(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return validator;
  }

  // FieldType:Number
  public validatorNumber(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = validator.concat(
      Validators.pattern(REGULAR_EXPRESSION.DECIMAL_PATTERN)
    );
    return validator;
  }

  // FieldType:Color
  public validatorColor(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return validator;
  }

  // FieldType:Integer
  public validatorInteger(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = validator.concat(
      Validators.pattern(REGULAR_EXPRESSION.NUMBER_PATTERN)
    );
    return validator;
  }

  // FieldType:TextArea
  public validatorTextArea(): Array<ValidatorFn> {
    let validator = new Array<ValidatorFn>();
    validator = null;
    return validator;
  }

  // FieldType:JSON
  public validatorJSON(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    validator.push(JSONSyntaxValidator.invalidJson);
    return validator;
  }

  // FieldType:Multi
  public validatorMulti(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    validator.push(JSONSyntaxValidator.invalidJson);
    return validator;
  }

  // FieldType:Select
  public validatorSelect(): Array<ValidatorFn> {
    const validator = new Array<ValidatorFn>();
    return validator;
  }

  public loadReportTemplateImages() {
    this.reportTemplateService
      .getReportTemplateImages(this.templateId)
      .subscribe((images: ReportTemplateImageList[]) => this.templateImages = images);
  }

  public ready() {
    this.onReady$.next('ready');
  }

  public getTargetIdList(): Observable<ResponseIdList[]> {
    return this.reportTemplateService.getModelsForTemplates(
      this.templateId,
      null
    );
  }

  public getReportTemplateImages(): { label: string; value?: string }[] {
    const tempImages: { label: string; value?: string }[] = [];
    this.templateImages.forEach((image: ReportTemplateImageList) => {
      tempImages.push({ label: image.fileName, value: image.originalUrl });
    });
    return tempImages;
  }

  public getReportDynamicDataFields(): any[] {
    const tempDataFields: { label: string; value?: string }[] = [];
    if (
      this.dynamicDataFields &&
      this.dynamicDataFields.Text &&
      this.dynamicDataFields.Text.length > 0
    ) {
      this.dynamicDataFields.Text.forEach((dataField: string) => {
        tempDataFields.push({ label: dataField, value: dataField });
      });
    } else if (this.isVisol && this.dynamicDataFields) {
      this.dynamicDataFields.forEach(
        (dataField: { label: string; value?: string }) => {
          tempDataFields.push(dataField);
        }
      );
    }
    return tempDataFields;
  }

  public getMeasurementPointDataFields(): any[] {
    const tempDataFields: { label: string; value?: string }[] = [];
    if (
      this.dynamicDataFields &&
      this.dynamicDataFields.MeasurementPointDataField
    ) {
      this.dynamicDataFields.MeasurementPointDataField.forEach(
        (dataField: string) => {
          tempDataFields.push({ label: dataField, value: dataField });
        }
      );
    }
    return tempDataFields;
  }

  public getVisualEventDataFields(): any[] {
    const tempDataFields: { label: string; value?: string }[] = [];
    this.dynamicDataFields.MeasurementVisualEventDataField.forEach(
      (dataField: string) => {
        tempDataFields.push({ label: dataField, value: dataField });
      }
    );
    return tempDataFields;
  }

  public getImageUrl(fileName: string): string {
    let imageURL: string;
    this.templateImages.forEach((image: ReportTemplateImageList) => {
      if (fileName === image.fileName) {
        imageURL = image.originalUrl;
      }
    });
    return imageURL;
  }

  public getElementType(): ElementType {
    return this.drawElementType;
  }

  public fitVirtualGrid(src: ReportPreviewMetadata): ReportPreviewMetadata {
    const calcGridPx = (value: number): number => {
      const useMinus: boolean = value < 0;
      const mod = Math.abs(value) % this.virtualGridPx;
      let rtnValue = Math.abs(value);

      rtnValue =
        mod < this.virtualGridPx / 2
          ? rtnValue - mod
          : rtnValue + (this.virtualGridPx - mod);
      rtnValue = useMinus && rtnValue !== 0 ? rtnValue * -1 : rtnValue;

      return rtnValue;
    };
    src.heightPx = calcGridPx(src.heightPx);
    src.widthPx = calcGridPx(src.widthPx);
    src.topPx = calcGridPx(src.topPx);
    src.leftPx = calcGridPx(src.leftPx);

    return src;
  }

  public getCommonProperties(): ReportProperties[] {
    return this.reportModel.getCommonProperties();
  }

  public getSelectedElementProperties(): ReportProperties[] {
    return this.reportModel.getSelectedElementProperties();
  }
  public getPageIds(): string[] {
    return this.reportModel.getPageIds();
  }

  public getPageNum(pageId: string) {
    let index = -1;
    const ids = this.reportModel.getPageIds();
    ids.some((id, i) => {
      if (id === pageId) {
        index = i;
        return true;
      }
      return false;
    });

    return index + 1;
  }

  public getPageTotalNum() {
    return this.reportModel.getPageIds().length;
  }
  public getTextIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Text');
  }
  public getLineIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Line');
  }
  public getImageIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Image');
  }
  public getRectangleIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Rectangle');
  }
  public getSummaryIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Summary');
  }
  public getPictureIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Picture');
  }
  public getDataPlateIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'DataPlate');
  }
  public getMeasurementPointIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'MeasurementPoint');
  }
  public getVisualEventsIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'VisualEvents');
  }
  public getSignatureIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Signature');
  }
  public getTableIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Table');
  }
  public getInteractiveIsoIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'InteractiveIso');
  }
  public getDropDownListIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'DropDownList');
  }

  public getSectionIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'Section');
  }
  public getCustomElementIds(pageId: string, location: Location): string[] {
    return this.reportModel.getIds(pageId, location, 'CustomElement');
  }

  public getPage(pageId: string): ReportPage {
    return this.reportModel.getPage(pageId);
  }

  public getPreviewPage(pageId: string): ReportPage {
    return this.reportModel.getPreviewPage(pageId);
  }
  public getText(textId: string): ReportText {
    return this.reportModel.getText(textId);
  }
  public getLine(lineId: string): ReportLine {
    return this.reportModel.getLine(lineId);
  }
  public getImage(imageId: string): ReportImage {
    return this.reportModel.getImage(imageId);
  }
  public getMeasurementPoints(tableId: string): ReportMeasurementPoint {
    return this.reportModel.getMeasurementPoint(tableId);
  }
  public getRectangle(rectangleId: string): ReportRectangle {
    return this.reportModel.getRectangle(rectangleId);
  }
  public getSummary(summaryId: string): ReportSummary {
    return this.reportModel.getSummary(summaryId);
  }
  /* public getPicture(pictureId: string): ReportPicture {
    return this.reportModel.getPicture(pictureId);
  } */
  public getDataPlate(dataPlateId: string): ReportDataPlate {
    return this.reportModel.getDataPlate(dataPlateId);
  }
  public getVisualEvents(visualEventId: string): ReportVisualEvents {
    return this.reportModel.getVisualEvent(visualEventId);
  }
  public getSignature(signatureId: string): ReportSignature {
    return this.reportModel.getSignature(signatureId);
  }
  public getSection(sectionId: string): ReportSection {
    return this.reportModel.getSection(sectionId);
  }
  public getTable(tableId: string): ReportTable {
    return this.reportModel.getTable(tableId);
  }
  public getInterActiveIso(isoId: string): ReportISO {
    return this.reportModel.getInterActiveIso(isoId);
  }
  public getDropDownList(ddlId: string): ReportDropDownList {
    return this.reportModel.getDropDownList(ddlId);
  }
  public getCustomElements(ceId: string): ReportCustomElement {
    return this.reportModel.getCustomElement(ceId);
  }

  public getPaperHeightPx(): number {
    return this.reportModel.getPaperHeightPx();
  }

  // テキストリストをバックエンドに送れる型に成型
  public textTypeShaping(): any {
    const tmpTemplate = JSON.parse(JSON.stringify(this.getTemplate())) as any;

    tmpTemplate.bodies?.forEach(page => {
      page.inspectionSummary?.table?.columns?.forEach(column => {
        column.texts = column.texts?.map(text => text?.text);
      });
      page.inspectionSummary?.subTotal?.table?.columns?.forEach(column => {
        column.texts = column.texts?.map(text => text?.text);
      });
    });

    tmpTemplate.bodies?.forEach(page => {
      page.inspectionPictures?.table?.columns?.forEach(column => {
        column.texts = column.texts?.map(text => text?.text);
      });
    });

    return tmpTemplate;
  }

  public getTemplate(): TemplateDefinition {
    const model = this.reportModel;
    const tempTemplate: TemplateDefinition = JSON.parse(
      JSON.stringify(this.tempTemplate.template, undefined, 4)
    );
    const tempHeader: TemplateCommonSection = tempTemplate.header;
    const headerTexts: {
      [elementId: string]: ReportText;
    } = model.getHeaderTexts();
    tempHeader.texts = [];
    for (const id of Object.keys(headerTexts)) {
      tempHeader.texts.push(headerTexts[id].getTemplateJSON());
    }
    const headerImages: {
      [elementId: string]: ReportImage;
    } = model.getHeaderImages();
    tempHeader.images = [];
    for (const id of Object.keys(headerImages)) {
      tempHeader.images.push(headerImages[id].getTemplateJSON());
    }
    const headerLines: {
      [elementId: string]: ReportLine;
    } = model.getHeaderLines();
    tempHeader.lines = [];
    for (const id of Object.keys(headerLines)) {
      tempHeader.lines.push(headerLines[id].getTemplateJSON());
    }
    const headerRectangles: { [elementId: string]: ReportRectangle } =
      model.getHeaderRectangles();
    for (const id of Object.keys(headerRectangles)) {
      tempHeader.rectangles.push(headerRectangles[id].getTemplateJSON());
    }
    const tempBodies: TemplatePageSection[] = tempTemplate.bodies;
    tempBodies.length = 0;
    for (const id of model.getPageIds()) {
      const tempPage: TemplatePageSection = model.getPage(id).getTemplateJSON();

      const bodyTextKeys: string[] = this.getTextIds(id, 'Body');
      tempPage.texts = [];
      for (const textId of bodyTextKeys) {
        tempPage.texts.push(this.getText(textId).getTemplateJSON());
      }
      const bodyLineKeys = this.getLineIds(id, 'Body');
      tempPage.lines = [];
      for (const lineId of bodyLineKeys) {
        tempPage.lines.push(this.getLine(lineId).getTemplateJSON());
      }
      const bodyImageKeys = this.getImageIds(id, 'Body');
      tempPage.images = [];
      for (const imageId of bodyImageKeys) {
        tempPage.images.push(this.getImage(imageId).getTemplateJSON());
      }
      const bodyRectangleKeys = this.getRectangleIds(id, 'Body');
      tempPage.rectangles = [];
      for (const rectangleId of bodyRectangleKeys) {
        tempPage.rectangles.push(
          this.getRectangle(rectangleId).getTemplateJSON()
        );
      }
      if (!this.isVisol) {
        const bodyMeasurementPointKeys = this.getMeasurementPointIds(
          id,
          'Body'
        );
        tempPage.measurementPoints = [];
        for (const measurementPointKey of bodyMeasurementPointKeys) {
          tempPage.measurementPoints.push(
            this.getMeasurementPoints(measurementPointKey).getTemplateJSON()
          );
        }
        const bodyVisualEventsKeys = this.getVisualEventsIds(id, 'Body');
        tempPage.visualEvents = [];
        for (const visualEventId of bodyVisualEventsKeys) {
          tempPage.visualEvents.push(
            this.getVisualEvents(visualEventId).getTemplateJSON()
          );
        }
        const bodySignatureKeys = this.getSignatureIds(id, 'Body');
        tempPage.signatures = [];
        for (const signatureId of bodySignatureKeys) {
          tempPage.signatures.push(
            this.getSignature(signatureId).getTemplateJSON()
          );
        }
        const bodySectionKeys = this.getSectionIds(id, 'Body');
        tempPage.sections = [];
        for (const sectionId of bodySectionKeys) {
          tempPage.sections.push(this.getSection(sectionId).getTemplateJSON());
        }
        const bodyTableKeys = this.getTableIds(id, 'Body');
        tempPage.tables = [];
        for (const tableId of bodyTableKeys) {
          tempPage.tables.push(this.getTable(tableId).getTemplateJSON());
        }
        const bodyInteractiveIsoKeys = this.getInteractiveIsoIds(id, 'Body');
        tempPage.interactiveIso = [];
        for (const isoId of bodyInteractiveIsoKeys) {
          tempPage.interactiveIso.push(
            this.getInterActiveIso(isoId).getTemplateJSON()
          );
        }
        const bodyDropDownListKeys = this.getDropDownListIds(id, 'Body');
        tempPage.dropDownLists = [];
        for (const ddlId of bodyDropDownListKeys) {
          tempPage.dropDownLists.push(
            this.getDropDownList(ddlId).getTemplateJSON()
          );
        }
        const bodyCustomElementKeys = this.getCustomElementIds(id, 'Body');
        tempPage.customElements = [];
        for (const ceId of bodyCustomElementKeys) {
          tempPage.customElements.push(
            this.getCustomElements(ceId).getTemplateJSON()
          );
        }
      } else {
        const bodySummaryKeys = this.getSummaryIds(id, 'Body');
        const summary = this.getSummary(bodySummaryKeys[0]);
        if (summary) {
          tempPage.inspectionSummary = summary.getTemplateJSON();
          /* console.log({
            json: summary.getTemplateJSON(),
            json2: summary
          }); */
        } else {
          tempPage.inspectionSummary = null;
        }
        const bodyPictureKeys = this.getPictureIds(id, 'Body');
        const picture = this.getSummary(bodyPictureKeys[0]);
        if (picture) {
          tempPage.inspectionPictures = picture.getTemplateJSON();
          /* console.log({
              json: picture.getTemplateJSON(),
              json2: picture
            }); */
        } else {
          tempPage.inspectionPictures = null;
        }
        const bodyDataPlateKeys = this.getDataPlateIds(id, 'Body');
        const dataPlate = this.getDataPlate(bodyDataPlateKeys[0]);
        if (dataPlate) {
          tempPage.dataPlates = dataPlate.getTemplateJSON();
        } else {
          tempPage.dataPlates = null;
        }
      }
      tempBodies.push(tempPage);
    }

    const tempFooter: TemplateCommonSection = tempTemplate.footer;
    const footerTexts: {
      [elementId: string]: ReportText;
    } = model.getFooterTexts();
    tempFooter.texts = [];
    for (const id of Object.keys(footerTexts)) {
      tempFooter.texts.push(footerTexts[id].getTemplateJSON());
    }
    const footerImages: {
      [elementId: string]: ReportImage;
    } = model.getFooterImages();
    tempFooter.images = [];
    for (const id of Object.keys(footerImages)) {
      tempFooter.images.push(footerImages[id].getTemplateJSON());
    }
    const footerLines: { [elementId: string]: ReportLine } =
      model.getFooterLines();

    tempFooter.lines = [];
    for (const id of Object.keys(footerLines)) {
      tempFooter.lines.push(footerLines[id].getTemplateJSON());
    }
    const footerRectangles: { [elementId: string]: ReportRectangle } =
      model.getFooterRectangles();

    tempFooter.rectangles = [];
    for (const id of Object.keys(footerRectangles)) {
      tempFooter.rectangles.push(footerRectangles[id].getTemplateJSON());
    }

    return tempTemplate;
  }
  // TODO 一時的にid付与のためのコード。別途削除
  /**
   * UUIDを生成する
   * 流用ロジック：https://github.com/GoogleChrome/chrome-platform-analytics/blob/master/src/internal/identifier.js
   * ライセンス  ：Apache License v2.0
   */
  private generateUUID(): string {
    const chars = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.split('');
    for (let i = 0, len = chars.length; i < len; i++) {
      switch (chars[i]) {
        case 'x':
          chars[i] = Math.floor(Math.random() * 16).toString(16);
          break;
        case 'y':
          chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16);
          break;
      }
    }
    return chars.join('');
  }

  public alignElementsWithSectionOnPage(pageId: string): void {
    const sectionIds = this.getSectionIds(pageId, 'Body');
    if (sectionIds.length === 0) {
      return;
    }
    this.reportModel.alignElementsWithSection(pageId);
  }

  public getStageName() {
    return this.stageNames;
  }

  private sortPreviewImage(a: PreviewImageDetail, b: PreviewImageDetail) {
    if (a == null && b == null) {
      return 0;
    } else if (a == null) {
      return 1;
    } else if (b == null) {
      return -1;
    }

    if (a.inspectionId == null && b.inspectionId == null) {
      return 0;
    } else if (a.inspectionId == null || a.inspectionId > b.inspectionId) {
      return 1;
    } else if (b.inspectionId == null || a.inspectionId < b.inspectionId) {
      return -1;
    }

    if (a.subjectLocationId == null && b.subjectLocationId == null) {
      return 0;
    } else if (a.subjectLocationId == null) {
      return 1;
    } else if (b.subjectLocationId == null) {
      return -1;
    }

    return (a.subjectLocationId < b.subjectLocationId ? -1 : 1);
  }

  public calcSummaryPage(inspectionImages: PreviewImageDetail[], reportLocations: ReportLocation[],
    subjectModel: SubjectModel, locList: SubjectLocation[]) {
    this.inspectionSummary = [];
    this.subtotal = {};
    const summaryPage = this.originTemplate.template.bodies.find(body => body.inspectionSummary != null);
    const summaryData = new Array<Array<TableData>>();
    let summaryLocations = [];

    if (summaryPage != null && summaryPage.inspectionSummary.isEmptyFolderDisplayed) {
      locList.forEach(loc => {
        const targetLoc = reportLocations.find(reportLoc => reportLoc.identifier === loc.identifier);
        if (targetLoc) {
          summaryLocations.push(targetLoc);
        } else {
          summaryLocations.push(new ReportLocation(loc.identifier));
        }
      });
    } else {
      summaryLocations = reportLocations;
    }

    if (summaryPage != null && inspectionImages.some(_ => !_.isDataPlate)) {
      const summaryPageBreakRow = summaryPage.inspectionSummary.defaultPageBreakRowCount;
      const dataMode = summaryPage.inspectionSummary.table.dataMode;

      const summaryDataGenerator = new ReportDataGenerator(subjectModel, inspectionImages, this.tenantId);
      const list = summaryDataGenerator.generateReportData(summaryLocations, subjectModel, dataMode);

      const inspectionLocations = JSON.parse(this.inspectionSchema.inspectionLocations);

      const isSelectedAllFolder = this.configSchemas.find(item => item.key === "IsSelectedAllFolderWhenCreatingReport");
      const shouldShowAllFolders = isSelectedAllFolder ? isSelectedAllFolder.value === "true" : false;

      summaryData.push(
        list.filter(data => {
          const hasDefinedImageId = data.images.length > 0 && data.images[0]?.id !== undefined && !data.images[0]?.isDummy;
          const isValidIdentifier = inspectionLocations.some(
            location => location.identifier === data.id && location.comment.trim() !== ""
          );
          return hasDefinedImageId || isValidIdentifier || shouldShowAllFolders;
        })
      );

      summaryData.forEach(data => {
        const summaryPageSeparator = new ReportDataGenerator(subjectModel, inspectionImages, this.tenantId);
        const pages = summaryPageSeparator.separatePageReportData(data, summaryPageBreakRow, 'Summary');
        pages.forEach(page => this.inspectionSummary.push(page));
        const groupBy = <K extends PropertyKey, V>(
          array: readonly V[],
          getKey: (cur: V, idx: number, src: readonly V[]) => K
        ) =>
          array.reduce((obj, cur, idx, src) => {
            const key = getKey(cur, idx, src);
            if (!obj[key]) {
              obj[key] = [];
            }
            obj[key].push(cur);
            return obj;
          }, {} as Partial<Record<K, V[]>>);

        if (summaryPage.inspectionSummary.subTotal !== null) {
          pages.forEach(page => {
            const group = groupBy(page, d => d.mark);
            const groupList = Object.entries(group);
            groupList.forEach(g => {
              const key = Number(g[0]);
              const count = g[1].length;
              if (this.subtotal[key] !== null && this.subtotal[key] !== undefined) {
                this.subtotal[key] += count;
              } else {
                this.subtotal[key] = count;
              }
            });
          });
        }
      });
    }

    return this.inspectionSummary;
  }

  public calcPicturePage(inspectionImages: PreviewImageDetail[], reportLocations: ReportLocation[],
    subjectModel: SubjectModel) {
    this.inspectionPictures = [];
    const picturePage = this.originTemplate.template.bodies.find(body => body.inspectionPictures != null);
    const isSelectedAllFolder = this.configSchemas.find(item => item.key === "IsSelectedAllFolderWhenCreatingReport");
    const shouldShowAllFolders = isSelectedAllFolder ? isSelectedAllFolder.value === "true" : false;

    if (picturePage != null && inspectionImages.some(_ => !_.isDataPlate)) {
      this.picturePageBreakRow = picturePage.inspectionPictures.defaultPageBreakRowCount;
      const dataMode = picturePage.inspectionPictures.table.dataMode;
      const picturesDataGenerator = new ReportDataGenerator(subjectModel, inspectionImages, this.tenantId);
      const list = picturesDataGenerator.generateReportData(reportLocations, subjectModel, dataMode);
      const tableData = shouldShowAllFolders
        ? list.filter(data => data.images.length)
        : list.filter(data => data.images.length > 0 && !data.images[0]?.isDummy);
      const pages = picturesDataGenerator.separatePageReportData(tableData, this.picturePageBreakRow, 'Picture');
      pages.forEach(page => this.inspectionPictures.push(page));
    }
  }

  public calcDataPlatePage(inspectionImages: PreviewImageDetail[], reportLocations: ReportLocation[],
    subjectModel: SubjectModel) {
    this.inspectionDataPlate = [];
    const dataPlatePage = this.originTemplate.template.bodies.find(body => body.dataPlates != null);

    if (dataPlatePage != null && inspectionImages.some(_ => _.isDataPlate)) {
      const dataPlateDataGenerator = new ReportDataGenerator(subjectModel, inspectionImages, this.tenantId);
      const dataPlateLocations = this.utils.getLocations(subjectModel, null, IMAGE_TYPE.DATAPLATE);
      const dataPlateImages = dataPlateDataGenerator.generateReportDataPlateImage(reportLocations, dataPlateLocations);
      this.inspectionDataPlate = this.chunk(dataPlateImages, 4);
    }
  }

  private chunk<T extends any[]>(arr: T, size: number) {
    return arr.reduce(
      (newArr, _, i) => (i % size ? newArr : [...newArr, arr.slice(i, i + size)]),
      [] as T[][]
    );
  }

  public setSpans(datas: TableData[], columns: TableColumn[]) {

    var spans: Array<Array<number>> = new Array<Array<number>>();
    for (let i:number =0; i < datas.length; i++ ) {
      spans.push(new Array<number>(columns.length));
    }

    // 縦の結合項目の洗い出し
    for (var i: number = 0; i < columns.length; i++) {
      // 1ページ内のData
      for (var j: number = 0; j < datas.length; j++) {
        if (columns[i].isCombined) {
          // 結合処理
          columns[i].textsInfo.forEach(text => {
            var property = new DynamicProperty(text.dynamicId);
            switch (property.key) {
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryLocationIdentifier:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesLocationIdentifier:
                if (datas[j].identifiers.length > property.index) {
                  datas[j]._value += datas[j].identifiers[property.index];
                }
                break;
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryLocationLowermostIdentifier:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesLocationLowermostIdentifier:
                datas[j]._value += datas[j].identifiers[datas[j].identifiers.length - 1];
                break;
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryLocationName:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesLocationName:
                if (datas[j].names.length > property.index) {
                  datas[j]._value += datas[j].names[property.index];
                }
                break;
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryLocationLowermostName:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesLocationLowermostName:
                datas[j]._value += datas[j].names[datas[j].names.length - 1];
                break;
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryInspectionFor:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesInspectionFor:
                datas[j]._value.concat(...datas[j].inspectionFor);
                break;
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryClass:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesClass:
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryMark:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesMark:
                datas[j]._value += datas[j].mark;
                break;
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryTitle:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesTitle:
                datas[j]._value += datas[j].title;
                break;
              case DYNAMIC_PROPERTY.KEY.InspectionSummaryComment:
              case DYNAMIC_PROPERTY.KEY.InspectionPicturesImage:
                datas[j]._value += datas[j].comment;
                break;
              case DYNAMIC_PROPERTY.KEY.Border:
              default:
                break;
            }
          });
          var group = groupBy(datas, '_value');
          this.CalculateSpan(group, i, spans);
        } else {
          // falseの場合は結合なしで1
          spans[j][i] = 1;
        }
      }
    }
    return spans;
  }

  private CalculateSpan(group, i: number, spans: number[][]) {
    var j = 0;
    Object.keys(group).forEach((key) => {
      var isFirst: boolean = true;
      for (var k: number = 0; k < group[key].length; k++) {
        spans[j + k][i] = isFirst ? group[key].length : 0;
        isFirst = false;
      }
      j += group[key].length;
    });
  }

  private removeExcludedInputGroupsFromTemplate(reportTemplate: ReportTemplate) {
    if (this.report.excludedInputGroups != null)
    {
      const bodies: TemplatePageSection[] = reportTemplate.template.bodies;
      this.report.excludedInputGroups.forEach(group => {
        bodies.forEach(body => {
          body.texts = body.texts.filter(t => t.inputGroup !== group);
        });
        reportTemplate.template.bodies = bodies.filter(body => !this.isPageEmpty(body));
      });
    }
  }

  private isPageEmpty(body: TemplatePageSection): boolean {
    return body.dataPlates == null
        && (body.images == null || body.images.length == 0)
        && body.inspectionPictures == null
        && body.inspectionSummary == null
        && (body.rectangles == null || body.rectangles.length == 0)
        && (body.texts == null || body.texts.length == 0);
  }

  public getpicturePageBreakRow() {
    return this.picturePageBreakRow;
  }
}
